import {
  type ComplianceForm,
  checkComplianceForm,
  DEFAULT_OPT_OUT_CONFIRMATION,
  DEFAULT_RESUBSCRIBE_CONFIRMATION,
  defaultHelpText,
  parseHelpContact,
  renderComplianceText,
} from '@fundline/shared/schemas/workspace-settings';
import { countSegments } from '@fundline/shared/sms/segments';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { TextAreaField, TextField } from '../../components/ui/Field.tsx';
import { Card, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { SettingsTabs } from '../settings/SettingsTabs.tsx';
import {
  type ComplianceSettingsData,
  useComplianceSettingsQuery,
  useSaveComplianceSettings,
} from './use-compliance-settings.ts';

/**
 * Settings → Compliance (M6): the opt-out footer and the automatic replies to STOP, START and HELP.
 * Every reply must fit one plain-text (GSM-7) segment once the workspace name and help contact are
 * filled in; the shared rules decide (the worker uses the same ones).
 */
export function ComplianceSettingsPage() {
  const { workspaceId } = useWorkspaceClaims();
  const settings = useComplianceSettingsQuery(workspaceId);
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Workspace settings"
        description="Opt-out wording and the automatic replies. Changes are recorded in the audit log."
      />
      <SettingsTabs />
      {settings.isPending ? <Spinner /> : null}
      {settings.isError ? <Alert tone="error">{settings.error.message}</Alert> : null}
      {settings.data ? <ComplianceSettingsForm data={settings.data} /> : null}
    </div>
  );
}

/** "1 segment · 96 characters", with a warning naming characters that force Unicode. */
function SegmentInfo({ text, testId }: { text: string; testId: string }) {
  const info = countSegments(text);
  const unicode = info.encoding === 'ucs2';
  return (
    <p
      className={`footnote ${info.segments > 1 || unicode ? 'text-danger' : 'text-ink-muted'}`}
      data-testid={testId}
    >
      {info.segments} {info.segments === 1 ? 'segment' : 'segments'} · {info.characters} characters
      {unicode
        ? ` · Unicode because of ${info.nonGsmCharacters.map((c) => `"${c}"`).join(', ')}`
        : ''}
    </p>
  );
}

function ComplianceSettingsForm({ data }: { data: ComplianceSettingsData }) {
  const { workspaceId } = useWorkspaceClaims();
  const save = useSaveComplianceSettings(workspaceId);
  const [form, setForm] = useState<ComplianceForm>(data.form);
  const [submitted, setSubmitted] = useState(false);
  const issues = checkComplianceForm(form, data.workspaceName);
  const shown = submitted ? issues : {};
  const contact = parseHelpContact(form.helpContact);
  const vars = {
    workspaceName: data.workspaceName,
    helpContact: contact.ok ? contact.contact : null,
  };
  const set = <K extends keyof ComplianceForm>(key: K, value: ComplianceForm[K]) => {
    save.reset();
    setForm((f) => ({ ...f, [key]: value }));
  };

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(issues).length > 0) return;
    save.mutate({ form, rawSettings: data.rawSettings });
  }

  const preview = (template: string, fallback: string) =>
    renderComplianceText(template.trim() === '' ? fallback : template, vars);
  const optOutPreview = preview(form.optOutConfirmation, DEFAULT_OPT_OUT_CONFIRMATION);
  const resubscribePreview = preview(
    form.resubscribeConfirmation,
    DEFAULT_RESUBSCRIBE_CONFIRMATION,
  );
  const helpPreview = preview(form.helpText, defaultHelpText(vars.helpContact));

  return (
    <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
      <Card title="Opt-out footer">
        <div className="flex flex-col gap-3">
          <TextField
            label="Footer"
            hint="Added on its own line. Counts toward the segment limit of the text it is added to."
            value={form.optOutFooter}
            onChange={(e) => set('optOutFooter', e.target.value)}
            error={shown.optOutFooter}
            maxLength={60}
          />
          <SegmentInfo text={form.optOutFooter} testId="footer-segments" />
          <label className="flex items-start gap-2 callout text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              checked={form.footerOnFirstMessageEver}
              onChange={(e) => set('footerOnFirstMessageEver', e.target.checked)}
            />
            Add it to the first text ever sent to each merchant
          </label>
        </div>
      </Card>

      <Card title="Automatic replies">
        <div className="flex flex-col gap-5">
          <p className="callout text-ink-muted">
            Sent at once, at any hour, when a merchant texts STOP, START or HELP. Leave a text empty
            to use the default. <code>{'{workspace}'}</code> becomes &ldquo;{data.workspaceName}
            &rdquo; and <code>{'{help_contact}'}</code> your help contact.
          </p>

          <div className="flex flex-col gap-2">
            <TextField
              label="Help contact"
              hint="An email address or phone number people can reach. HELP texts get no automatic reply without it."
              value={form.helpContact}
              onChange={(e) => set('helpContact', e.target.value)}
              error={shown.helpContact}
              placeholder="help@yourcompany.com"
              autoComplete="off"
            />
            {form.helpContact.trim() === '' ? (
              <Alert tone="warning">
                No help contact yet: a merchant who texts HELP gets no automatic reply, and you are
                notified to answer yourself.
              </Alert>
            ) : null}
          </div>

          <ReplyField
            label="Reply to HELP"
            value={form.helpText}
            placeholder={defaultHelpText(vars.helpContact)}
            onChange={(v) => set('helpText', v)}
            error={shown.helpText}
            preview={helpPreview}
            testId="help"
          />
          <ReplyField
            label="Reply to STOP (opt-out confirmation)"
            value={form.optOutConfirmation}
            placeholder={DEFAULT_OPT_OUT_CONFIRMATION}
            onChange={(v) => set('optOutConfirmation', v)}
            error={shown.optOutConfirmation}
            preview={optOutPreview}
            testId="opt-out"
          />
          <ReplyField
            label="Reply to START (resubscribe confirmation)"
            value={form.resubscribeConfirmation}
            placeholder={DEFAULT_RESUBSCRIBE_CONFIRMATION}
            onChange={(v) => set('resubscribeConfirmation', v)}
            error={shown.resubscribeConfirmation}
            preview={resubscribePreview}
            testId="resubscribe"
          />
        </div>
      </Card>

      {submitted && Object.keys(issues).length > 0 ? (
        <Alert tone="error">Fix the highlighted fields first.</Alert>
      ) : null}
      {save.isError ? <Alert tone="error">{save.error.message}</Alert> : null}
      {save.isSuccess ? <Alert tone="success">Compliance settings saved.</Alert> : null}
      <div>
        <Button type="submit" busy={save.isPending}>
          Save compliance settings
        </Button>
      </div>
    </form>
  );
}

function ReplyField({
  label,
  value,
  placeholder,
  onChange,
  error,
  preview,
  testId,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  error: string | undefined;
  preview: string;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <TextAreaField
        label={label}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        error={error}
        rows={2}
        maxLength={200}
      />
      <div className="rounded-md bg-surface px-3 py-2 callout text-ink ring-1 ring-hairline">
        <p className="footnote font-medium text-ink-muted">Merchants receive</p>
        <p className="break-words" data-testid={`${testId}-preview`}>
          {preview}
        </p>
      </div>
      <SegmentInfo text={preview} testId={`${testId}-segments`} />
    </div>
  );
}
